import React, { useRef, useState } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  useTheme, 
  Grid,
  Card,
  IconButton,
  useMediaQuery,
  Paper,
  Divider,
  Stack,
  Chip,
  Dialog,
  DialogContent
} from '@mui/material';
import { 
  MusicNote as MusicNoteIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Notifications as NotificationsIcon,
  Translate as TranslateIcon,
  PhoneIphone as PhoneIcon,
  ArrowForward as ArrowForwardIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Speed as SpeedIcon,
  GraphicEq as TunerIcon,
  QueueMusic as PadIcon,
  PictureAsPdf as PdfIcon,
  EventBusy as BlockoutIcon,
  YouTube as YouTubeIcon,
  DarkMode as DarkModeIcon,
  Email as EmailIcon,
  AttachMoney as MoneyIcon,
  PlayCircleOutline as PlayIcon,
  PhoneIphone as PhoneIphoneIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const LandingPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { scrollY } = useScroll();
  const { t, i18n } = useTranslation();
  
  const [videoOpen, setVideoOpen] = useState(false);
  
  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };
  
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const practiceRef = useRef(null);
  const pricingRef = useRef(null);
  const comparisonRef = useRef(null);

  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 300], [1, 0.95]);

  const scrollToNextSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const coreFeatures = [
    { icon: <ScheduleIcon fontSize="large" />, titleKey: 'landing.features.servicePlanning.title', descKey: 'landing.features.servicePlanning.description' },
    { icon: <PeopleIcon fontSize="large" />, titleKey: 'landing.features.teamManagement.title', descKey: 'landing.features.teamManagement.description' },
    { icon: <MusicNoteIcon fontSize="large" />, titleKey: 'landing.features.songLibrary.title', descKey: 'landing.features.songLibrary.description' },
    { icon: <NotificationsIcon fontSize="large" />, titleKey: 'landing.features.notifications.title', descKey: 'landing.features.notifications.description' },
    { icon: <TranslateIcon fontSize="large" />, titleKey: 'landing.features.multilingual.title', descKey: 'landing.features.multilingual.description' },
    { icon: <PhoneIcon fontSize="large" />, titleKey: 'landing.features.mobileApp.title', descKey: 'landing.features.mobileApp.description' },
  ];

  const practiceTools = [
    { icon: <SpeedIcon sx={{ fontSize: 40 }} />, titleKey: 'landing.practiceTools.metronome.title', descKey: 'landing.practiceTools.metronome.description', color: '#FF6B6B' },
    { icon: <TunerIcon sx={{ fontSize: 40 }} />, titleKey: 'landing.practiceTools.tuner.title', descKey: 'landing.practiceTools.tuner.description', color: '#4ECDC4' },
    { icon: <PadIcon sx={{ fontSize: 40 }} />, titleKey: 'landing.practiceTools.worshipPad.title', descKey: 'landing.practiceTools.worshipPad.description', color: '#A259FF' },
    { icon: <MusicNoteIcon sx={{ fontSize: 40 }} />, titleKey: 'landing.practiceTools.capoHelper.title', descKey: 'landing.practiceTools.capoHelper.description', color: '#FFB347' },
  ];

  const moreFeatures = [
    { icon: <YouTubeIcon />, labelKey: 'landing.moreFeatures.youtube' },
    { icon: <DarkModeIcon />, labelKey: 'landing.moreFeatures.darkMode' },
    { icon: <PdfIcon />, labelKey: 'landing.moreFeatures.pdfExport' },
    { icon: <BlockoutIcon />, labelKey: 'landing.moreFeatures.blockoutDates' },
    { icon: <EmailIcon />, labelKey: 'landing.moreFeatures.welcomeEmails' },
    { icon: <MusicNoteIcon />, labelKey: 'landing.moreFeatures.chordTransposition' },
    { icon: <PeopleIcon />, labelKey: 'landing.moreFeatures.birthdayTracking' },
    { icon: <ScheduleIcon />, labelKey: 'landing.moreFeatures.frequencyTracking' },
  ];

  const comparisonRows = [
    { featureKey: 'landing.comparison.rows.price', prelude: true, preludeTextKey: 'landing.comparison.rows.pricePrelude', others: false, othersTextKey: 'landing.comparison.rows.priceOthers' },
    { featureKey: 'landing.comparison.rows.freeMusicians', prelude: true, preludeTextKey: 'landing.comparison.rows.freeMusiciansPrelude', others: false, othersTextKey: 'landing.comparison.rows.freeMusiciansOthers' },
    { featureKey: 'landing.comparison.rows.practiceTools', prelude: true, preludeTextKey: 'landing.comparison.rows.practiceToolsPrelude', others: false, othersTextKey: 'landing.comparison.rows.practiceToolsOthers' },
    { featureKey: 'landing.comparison.rows.multilingual', prelude: true, preludeTextKey: 'landing.comparison.rows.multilingualPrelude', others: false, othersTextKey: 'landing.comparison.rows.multilingualOthers' },
    { featureKey: 'landing.comparison.rows.pushNotifications', prelude: true, preludeTextKey: 'landing.comparison.rows.pushNotificationsPrelude', others: true, othersTextKey: 'landing.comparison.rows.pushNotificationsOthers' },
    { featureKey: 'landing.comparison.rows.songManagement', prelude: true, preludeTextKey: 'landing.comparison.rows.songManagementPrelude', others: true, othersTextKey: 'landing.comparison.rows.songManagementOthers' },
  ];

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* Top Bar for Login */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, p: { xs: 2, md: 3 }, zIndex: 10, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
          <IconButton 
            onClick={() => changeLanguage('pt')} 
            size="small" 
            sx={{ 
              opacity: i18n.language.startsWith('pt') ? 1 : 0.5, 
              '&:hover': { opacity: 1 },
              fontSize: '1.2rem'
            }}
          >
            🇧🇷
          </IconButton>
          <IconButton 
            onClick={() => changeLanguage('en')} 
            size="small" 
            sx={{ 
              opacity: i18n.language.startsWith('en') ? 1 : 0.5, 
              '&:hover': { opacity: 1 },
              fontSize: '1.2rem'
            }}
          >
            🇺🇸
          </IconButton>
          <IconButton 
            onClick={() => changeLanguage('es')} 
            size="small" 
            sx={{ 
              opacity: i18n.language.startsWith('es') ? 1 : 0.5, 
              '&:hover': { opacity: 1 },
              fontSize: '1.2rem'
            }}
          >
            🇪🇸
          </IconButton>
        </Stack>
        <Button
          variant="outlined"
          onClick={() => navigate('/login', { replace: true })}
          sx={{ 
            color: 'white', 
            borderColor: 'rgba(255,255,255,0.3)',
            borderRadius: '20px',
            px: 3,
            '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          {t('landing.hero.signIn')}
        </Button>
      </Box>

      {/* Hero Section */}
      <Box
        ref={heroRef}
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 15, md: 22 },
          pb: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#000000',
        }}
      >
        {/* Glowing Orbs */}
        <Box sx={{
          position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)',
          width: '100%', height: '80%',
          background: 'radial-gradient(ellipse at center, rgba(124, 58, 237, 0.2) 0%, rgba(0,0,0,0) 70%)',
          pointerEvents: 'none', zIndex: 0
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: '850px', mx: 'auto' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <Chip 
                label={t('landing.hero.badge')}
                sx={{ 
                  mb: 4, 
                  bgcolor: 'rgba(255,255,255,0.05)', 
                  color: 'rgba(255,255,255,0.9)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  py: 0.5,
                  backdropFilter: 'blur(10px)'
                }} 
              />
              <Typography
                variant="h1"
                sx={{
                  color: 'transparent',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  backgroundImage: 'linear-gradient(180deg, #FFFFFF 0%, #A5A5A5 100%)',
                  fontWeight: 800,
                  fontSize: { xs: '2.8rem', sm: '4rem', md: '5.5rem' },
                  mb: 3,
                  lineHeight: 1.05,
                  letterSpacing: '-0.03em'
                }}
              >
                {t('landing.hero.title')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  mb: 5,
                  fontWeight: 300,
                  lineHeight: 1.6,
                  maxWidth: '600px',
                  mx: 'auto',
                  fontSize: { xs: '1.1rem', md: '1.3rem' }
                }}
              >
                {t('landing.hero.subtitle')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={() => navigate('/register')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    borderRadius: '28px', 
                    px: 5, 
                    py: 1.8,
                    fontWeight: 'bold',
                    fontSize: '1.05rem',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 30px rgba(0,0,0,0.3)' },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {t('landing.hero.getStarted')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setVideoOpen(true)}
                  startIcon={<PlayIcon />}
                  sx={{ 
                    color: 'white', 
                    borderColor: 'rgba(255,255,255,0.5)',
                    borderRadius: '28px',
                    px: 5,
                    py: 1.8,
                    fontSize: '1.05rem',
                    '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  {t('landing.hero.watchVideo')}
                </Button>
              </Stack>
            </motion.div>
          </Box>
          
          {/* Mockup Image */}
          <Box 
            component={motion.div}
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            sx={{ 
              mt: { xs: 8, md: 12 }, 
              position: 'relative', 
              width: '100%', 
              mx: 'auto',
              maxWidth: '1000px',
              perspective: '1000px'
            }}
          >
            <Box
              sx={{
                width: '100%',
                pb: '60%', // Aspect ratio 16:9 approx
                background: 'url(https://placehold.co/1200x800/111/fff?text=App+Mockup) center/cover no-repeat',
                borderRadius: '24px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 30px 60px -10px rgba(0,0,0,0.8), 0 0 100px -20px rgba(124, 58, 237, 0.4)',
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Core Features Section */}
      <Box ref={featuresRef} sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#121212' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              variant="h2"
              component={motion.h2}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              sx={{ mb: 2, color: 'white', fontWeight: 'bold', fontSize: { xs: '2rem', md: '2.5rem' } }}
            >
              {t('landing.features.title')}
            </Typography>
            <Typography
              variant="h6"
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: '700px', mx: 'auto', fontWeight: 'normal' }}
            >
              {t('landing.features.subtitle')}
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            {coreFeatures.map((feature, index) => {
              const mdSize = (index === 0 || index === 5) ? 8 : 4;
              const isLarge = mdSize === 8;
              
              return (
              <Grid item xs={12} sm={isLarge ? 12 : 6} md={mdSize} key={index}>
                <Card
                  component={motion.div}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1, ease: 'easeOut' }}
                  whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(0,0,0,0.4)', borderColor: 'rgba(255,255,255,0.2)' }}
                  sx={{
                    height: '100%', display: 'flex', flexDirection: 'column',
                    borderRadius: '24px', 
                    border: '1px solid rgba(255,255,255,0.08)',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', height: '100%', zIndex: 2, position: 'relative' }}>
                    <Box sx={{
                      color: theme.palette.primary.light, 
                      backgroundColor: 'rgba(124, 58, 237, 0.15)',
                      p: 1.5, borderRadius: '14px', mb: 3,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      alignSelf: 'flex-start',
                      border: '1px solid rgba(124, 58, 237, 0.3)'
                    }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1.5, color: 'white', letterSpacing: '-0.02em' }}>
                      {t(feature.titleKey)}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, mb: isLarge ? 4 : 0 }}>
                      {t(feature.descKey)}
                    </Typography>
                    
                    {isLarge && (
                      <Box sx={{ 
                        mt: 'auto', 
                        height: '180px', 
                        width: '100%', 
                        background: 'url(https://placehold.co/800x400/111/fff?text=Feature+Mockup) center top/cover no-repeat',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.1)',
                        transform: 'translateY(20px)',
                        transition: 'transform 0.3s ease',
                        '.MuiCard-root:hover &': {
                          transform: 'translateY(10px)'
                        }
                      }} />
                    )}
                  </Box>
                </Card>
              </Grid>
            )})}
          </Grid>
        </Container>
      </Box>

      {/* Practice Tools Section */}
      <Box
        ref={practiceRef}
        sx={{
          py: { xs: 8, md: 12 },
          background: '#0a0a0a',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Chip 
              label={t('landing.practiceTools.badge')}
              sx={{ mb: 2, bgcolor: 'rgba(162,89,255,0.3)', color: '#d4a5ff', fontWeight: 'bold' }} 
            />
            <Typography
              variant="h2"
              component={motion.h2}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              sx={{ mb: 2, color: 'white', fontWeight: 'bold', fontSize: { xs: '2rem', md: '2.5rem' } }}
            >
              {t('landing.practiceTools.title')}
            </Typography>
            <Typography
              variant="h6"
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: '600px', mx: 'auto', fontWeight: 'normal' }}
            >
              {t('landing.practiceTools.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {practiceTools.map((tool, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  component={motion.div}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8, boxShadow: `0 12px 40px ${tool.color}44`, borderColor: `${tool.color}88` }}
                  sx={{
                    height: '100%', borderRadius: '16px',
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Box sx={{
                      width: 70, height: 70, borderRadius: '16px',
                      background: `${tool.color}22`, color: tool.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mx: 'auto', mb: 2,
                    }}>
                      {tool.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}>
                      {t(tool.titleKey)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
                      {t(tool.descKey)}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Social Proof Section */}
      <Box sx={{ py: 8, backgroundColor: '#050505', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', mb: 5, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 600 }}>
            {t('landing.social.trustedBy', 'Trusted by worship teams from')}
          </Typography>
          <Stack direction="row" flexWrap="wrap" justifyContent="center" alignItems="center" gap={{ xs: 4, md: 8 }} sx={{ opacity: 0.5 }}>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, fontFamily: 'monospace' }}>HILLSONG</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 900, letterSpacing: '-2px' }}>elevation</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 300, fontStyle: 'italic' }}>Bethel Music</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, letterSpacing: '4px' }}>GATEWAY</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>PLANETSHAKERS</Typography>
          </Stack>
        </Container>
      </Box>

      {/* More Features Chips */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: '#1a1a1a' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              component={motion.h3}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}
            >
              {t('landing.moreFeatures.title')}
            </Typography>
          </Box>
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}
          >
            {moreFeatures.map((f, i) => (
              <Chip
                key={i}
                icon={f.icon}
                label={t(f.labelKey)}
                variant="outlined"
                sx={{ 
                  py: 2.5, px: 1, fontSize: '0.9rem', borderRadius: '12px',
                  bgcolor: '#0f0f0f', color: 'white',
                  borderColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: theme.palette.primary.main + '33', borderColor: theme.palette.primary.main },
                  '& .MuiChip-icon': { color: 'rgba(255,255,255,0.7)' }
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box
        ref={pricingRef}
        sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#000000', borderTop: '1px solid rgba(255,255,255,0.05)' }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="h2"
              component={motion.h2}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              sx={{ mb: 2, color: 'white', fontWeight: 'bold', fontSize: { xs: '2rem', md: '2.5rem' } }}
            >
              {t('landing.pricing.title')}
            </Typography>
            <Typography
              variant="h6"
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: '600px', mx: 'auto', fontWeight: 'normal' }}
            >
              {t('landing.pricing.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {/* Musicians - Free */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                sx={{ 
                  height: '100%', borderRadius: '24px', p: 4, 
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'white',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Typography variant="overline" color="rgba(255,255,255,0.5)">{t('landing.pricing.musician.label')}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, my: 1, color: 'white' }}>{t('landing.pricing.musician.price')}</Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.6)" sx={{ mb: 3 }}>{t('landing.pricing.musician.description')}</Typography>
                <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />
                {['feat1','feat2','feat3','feat4','feat5'].map((f) => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckIcon sx={{ color: theme.palette.success.main, mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2">{t(`landing.pricing.musician.${f}`)}</Typography>
                  </Box>
                ))}
                <Button
                  fullWidth variant="outlined" color="primary" size="large"
                  onClick={() => navigate('/register')}
                  sx={{ mt: 3, borderRadius: '12px', py: 1.5, fontWeight: 'bold' }}
                >
                  {t('landing.pricing.musician.cta')}
                </Button>
              </Card>
            </Grid>

            {/* Admin - $9.99 */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                component={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                sx={{
                  height: '100%', borderRadius: '24px', p: 4,
                  background: 'linear-gradient(180deg, rgba(124, 58, 237, 0.1) 0%, rgba(255,255,255,0.02) 100%)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 8px 40px rgba(124, 58, 237, 0.2)`,
                  position: 'relative',
                  color: 'white'
                }}
              >
                <Chip 
                  label={t('landing.pricing.admin.badge')} 
                  color="primary" 
                  size="small" 
                  sx={{ position: 'absolute', top: 20, right: 20, fontWeight: 'bold' }} 
                />
                <Typography variant="overline" color="rgba(255,255,255,0.5)">{t('landing.pricing.admin.label')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', my: 1 }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: 'white' }}>$10</Typography>
                  <Typography variant="body1" color="rgba(255,255,255,0.6)" sx={{ ml: 0.5 }}>/mo</Typography>
                </Box>
                <Chip size="small" color="secondary" label={t('landing.pricing.admin.noUserFees')} sx={{ mb: 2, fontWeight: 'bold', bgcolor: 'rgba(162, 89, 255, 0.2)', color: '#d4a5ff' }} />
                <Typography variant="body2" color="rgba(255,255,255,0.6)" sx={{ mb: 3 }}>{t('landing.pricing.admin.description')}</Typography>
                <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />
                {['feat1','feat2','feat3','feat4','feat5','feat6'].map((f) => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckIcon sx={{ color: theme.palette.success.main, mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2">{t(`landing.pricing.admin.${f}`)}</Typography>
                  </Box>
                ))}
                <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                  <Button
                    fullWidth variant="contained" size="large"
                    href="https://apps.apple.com/app/prelude"
                    target="_blank"
                    startIcon={<PhoneIphoneIcon />}
                    sx={{ borderRadius: '12px', py: 1.5, fontWeight: 'bold', bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
                  >
                    App Store
                  </Button>
                  <Button
                    fullWidth variant="contained" size="large"
                    href="https://play.google.com/store/apps/details?id=com.prelude"
                    target="_blank"
                    sx={{ borderRadius: '12px', py: 1.5, fontWeight: 'bold', bgcolor: '#01875f', '&:hover': { bgcolor: '#016b4d' } }}
                  >
                    Google Play
                  </Button>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Comparison Section */}
      <Box
        ref={comparisonRef}
        sx={{
          py: { xs: 8, md: 12 },
          background: '#0a0a0a', borderTop: '1px solid rgba(255,255,255,0.05)'
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="h2"
              component={motion.h2}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              sx={{ mb: 2, color: 'white', fontWeight: 'bold', fontSize: { xs: '2rem', md: '2.5rem' } }}
            >
              {t('landing.comparison.title')}
            </Typography>
            <Typography
              variant="h6"
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: '600px', mx: 'auto', fontWeight: 'normal' }}
            >
              {t('landing.comparison.subtitle')}
            </Typography>
          </Box>

          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            sx={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}
          >
            <Box sx={{ p: 2.5, bgcolor: '#1a1a1a' }}>
              <Grid container>
                <Grid item xs={5}><Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'white' }}>{t('landing.comparison.feature')}</Typography></Grid>
                <Grid item xs={3.5} sx={{ textAlign: 'center' }}><Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>Prelude</Typography></Grid>
                <Grid item xs={3.5} sx={{ textAlign: 'center' }}><Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'rgba(255,255,255,0.6)' }}>{t('landing.comparison.others')}</Typography></Grid>
              </Grid>
            </Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
            {comparisonRows.map((row, index) => (
              <Box key={index}>
                <Box sx={{ p: 2.5, bgcolor: index % 2 === 0 ? '#0f0f0f' : '#1a1a1a' }}>
                  <Grid container alignItems="center">
                    <Grid item xs={5}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'white' }}>{t(row.featureKey)}</Typography>
                    </Grid>
                    <Grid item xs={3.5} sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {row.prelude ? 
                          <CheckIcon sx={{ color: '#4ade80', fontSize: 22 }} /> : 
                          <CloseIcon sx={{ color: '#f87171', fontSize: 22 }} />
                        }
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{t(row.preludeTextKey)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3.5} sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {row.others ? 
                          <CheckIcon sx={{ color: '#4ade80', fontSize: 22 }} /> : 
                          <CloseIcon sx={{ color: '#f87171', fontSize: 22 }} />
                        }
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{t(row.othersTextKey)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                {index < comparisonRows.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />}
              </Box>
            ))}
          </Paper>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 10, md: 16 }, textAlign: 'center', backgroundColor: '#000000', position: 'relative', overflow: 'hidden' }}>
        {/* Glowing Orbs */}
        <Box sx={{
          position: 'absolute', bottom: '-30%', left: '50%', transform: 'translateX(-50%)',
          width: '100%', height: '100%',
          background: 'radial-gradient(ellipse at bottom, rgba(124, 58, 237, 0.2) 0%, rgba(0,0,0,0) 60%)',
          pointerEvents: 'none', zIndex: 0
        }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography
              variant="h2"
              sx={{ color: 'white', mb: 3, fontWeight: 'bold', fontSize: { xs: '2rem', md: '2.5rem' } }}
            >
              {t('landing.cta.title')}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: 'rgba(255,255,255,0.8)', mb: 5, maxWidth: '600px', mx: 'auto', fontWeight: 'normal' }}
            >
              {t('landing.cta.subtitle')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained" color="primary" size="large"
                onClick={() => navigate('/register')}
                endIcon={<ArrowForwardIcon />}
                sx={{ 
                  borderRadius: '28px', px: 5, py: 1.8, fontWeight: 'bold', fontSize: '1.05rem',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 30px rgba(0,0,0,0.3)' },
                  transition: 'all 0.3s ease'
                }}
              >
                {t('landing.cta.button')}
              </Button>
            </Stack>
            <Typography variant="body2" sx={{ mt: 3, color: 'rgba(255,255,255,0.6)' }}>
              {t('landing.cta.noCard')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, backgroundColor: theme.palette.grey[900], textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          © {new Date().getFullYear()} Prelude. {t('landing.footer.rights')}
        </Typography>
      </Box>
      {/* Video Modal */}
      <Dialog 
        open={videoOpen} 
        onClose={() => setVideoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#000',
            borderRadius: '16px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 0, height: { xs: '40vh', md: '60vh' } }}>
          <iframe 
            width="100%" 
            height="100%" 
            src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1" 
            title="Prelude Video" 
            frameBorder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowFullScreen
          ></iframe>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LandingPage;
